import { bbox, bboxClip } from "@turf/turf";
import { SettingsManager } from "@viamap/viamap2-common";
import { PDFDocument, PDFFont, PDFPage, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { splitIntoPartsStable } from "src/components/DatarapportShared";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";


type Block = { left: number, top: number, width: number, height: number };
export class MITDocument {
   pdf: PDFDocument;
   currentPage: PDFPage | null = null;
   currentBlock: Block | null = null;
   elements: MITDocumentElement[] = [];
   header: MITDocumentHeader | null = null;
   footer: MITDocumentFooter | null = null;
   font: PDFFont | null = null;
   showBounds: boolean = false;

   title: string = "";
   mVersion: string = "";
   date: Date = new Date();
   pageNr: string = "{i} / {t}";

   constructor(pdf: PDFDocument) {
      this.pdf = pdf;
      pdf.registerFontkit(fontkit)
      return this;
   }

   async save() {
      await this._writePDF();
      return this.pdf.save();
   }

   async embedFont(url: string): Promise<PDFFont> {
      const fontBytes = await fetch(url).then(res => res.arrayBuffer());
      const font = await this.pdf.embedFont(fontBytes);
      this.font = font;
      return font
   }

   debug() {
      this.showBounds = true;
   }

   add<T extends MITDocumentElement>(element: T): T {
      this.elements.push(element);
      return element;
   }

   setTitle(arg: string) { this.title = arg; return this; }
   setMVersion(arg: string) { this.mVersion = arg; return this; }
   setDate(arg: Date) { this.date = arg; return this; }
   setPageNr(arg: string) { this.pageNr = arg; return this; }

   nextPage(): Block {
      let page = this.pdf.addPage();
      if (this.font) page.setFont(this.font);
      this.currentPage = page;
      this.currentBlock = { left: 30, top: 796, width: 595 - 60, height: 800 - 30 };
      return this.currentBlock;
   }

   async _writePDF() {

      this.nextPage();
      for (const element of this.elements) {
         let blokE = await element.render(this);
         this.currentBlock!.top -= blokE.height;
         this.currentBlock!.height -= blokE.height;
      }


      let dateString = Intl.DateTimeFormat("da-dk", { dateStyle: "short", timeStyle: undefined }).format(new Date());
      this.pdf.getPages().forEach((page, x) => {
         let topY = page.getSize().height - 30;
         if (x == 0) {
            topY = 38;
         }


         page.setFontSize(8);

         page.drawText(this.title, { x: 30, y: topY });
         page.drawText("Mapit v" + this.mVersion, { x: 30, y: topY - 10 });
         let oYDate = Utils.measureTextWidth(dateString, 8);
         page.drawText(dateString, { x: 595 - 30 - oYDate, y: topY });
         let pageText = this.pageNr.replace("{i}", String(x + 1)).replace("{t}", String(this.pdf.getPages().length));
         let oYPage = Utils.measureTextWidth(pageText, 8);
         page.drawText(pageText, { x: 595 - 30 - oYPage, y: topY - 10 });
      });
   }

}

class MITDocumentElement {
   showBounds: boolean = false;
   parent: MITDocumentElement | null = null;

   constructor(parent?: MITDocumentElement) {
      this.parent = parent ?? null;
      return this;
   }

   notHeight(doc: MITDocument, usedHeight: number, requiredHeight: number): boolean {
      if ((doc.currentBlock?.height || 0) - usedHeight >= requiredHeight) {
         return false;
      }
      this.nextBlock(doc);
      return true;
   }

   nextBlock(doc: MITDocument): Block {
      let x = this.parent?.nextBlock(doc);
      if (!x) {
         return doc.nextPage();
      }
      return x;
   }

   // oxlint-disable-next-line no-unused-vars
   async render(doc: MITDocument): Promise<Block> {
      return { left: 0, top: 0, width: 0, height: 0 };
   }
}

export class MITDocumentAbsolute extends MITDocumentElement {
   x: number;
   y: number;
   width: number;
   height: number;
   elememts: MITDocumentElement[] = [];

   constructor(x: number, y: number, width: number, height: number) {
      super();
      this.x = x;
      this.y = y;
      this.width = width;
      this.height = height;
      return this;
   }

   add<T extends MITDocumentElement>(element: T): this {
      console.log("Adding element to absolute", element);
      this.elememts.push(element);
      return this;
   }

   async render(doc: MITDocument): Promise<Block> {
      let page = doc.currentPage;
      if (!page) {
         throw new Error("No current page to render on");
      }
      let store = doc.currentBlock;

      // page.drawRectangle({ x: this.x, y: this.y - this.height, width: this.width, height: this.height, borderColor: rgb(0.9, 0.9, 0.9), borderWidth: .1 });

      for (const element of this.elememts) {
         doc.currentBlock = { left: this.x, top: this.y, width: this.width, height: this.height };
         await element.render(doc);
      }
      doc.currentBlock = store;
      return { left: store?.left ?? 0, top: store?.top ?? 0, width: this.width, height: 0 };
   }
}

export class MITDocumentTile extends MITDocumentElement {
   height: number = 100;
   opacity: number = 1;
   url: string;
   hueRotate: number = 0;

   constructor(url: string) {
      super();
      this.url = url;
      return this;
   }

   setOpacity(opacity: number) {
      this.opacity = opacity;
      return this;
   }

   setHeight(height: number) {
      this.height = height;
      return this;
   }

   setHueRotate(deg: number) {
      this.hueRotate = deg;
      return this;
   }

   async render(doc: MITDocument): Promise<Block> {
      let block = doc.currentBlock ?? this.nextBlock(doc);
      console.log("Rendering tile with block", block);
      let imageBytes = await fetch(this.url).then(res => res.arrayBuffer());

      // hueRotate
      if (this.hueRotate !== 0) {
         // Apply hue rotation to imageBytes
         let canvas = document.createElement("canvas");;
         let img = document.createElement("img");
         let imageBlob = new Blob([imageBytes], { type: "image/png" });
         img.src = URL.createObjectURL(imageBlob);
         await new Promise((res) => { img.onload = res; });
         canvas.width = img.width;
         canvas.height = img.height;
         let ctx = canvas.getContext("2d");
         if (ctx) {
            ctx.drawImage(img, 0, 0, img.width, img.height);
            ctx.globalCompositeOperation = "hue";
            ctx.fillStyle = `hsl(${this.hueRotate}, 100%, 50%)`;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            imageBlob = await new Promise((res) => { canvas.toBlob(res as any); }) as Blob;
            imageBytes = await imageBlob.arrayBuffer();
         }
      }


      let image = await doc.pdf.embedPng(imageBytes);
      let scaledHeight = this.height;
      let scale = scaledHeight / image.height;
      let scaledWidth = image.width * scale;

      let repeats = Math.ceil(block.width / scaledWidth);
      for (let i = 0; i < repeats; i++) {
         doc.currentPage?.drawImage(image, {
            x: block.left + i * Math.round(scaledWidth),
            y: block.top - Math.round(scaledHeight),
            width: Math.round(scaledWidth),
            height: Math.round(scaledHeight),
            opacity: this.opacity,
         });
      }
      return { left: block.left, top: block.top - scaledHeight, width: block.width, height: scaledHeight };
   }

}
export class MITDocumentSquare extends MITDocumentElement {
   _w: number;
   _h: number;

   constructor(width: number, height: number) {
      super();
      this._w = width;
      this._h = height;
      return this;
   }

   async render(doc: MITDocument): Promise<Block> {
      return { left: doc.currentBlock?.left ?? 0, top: (doc.currentBlock?.top ?? 0) - this._h, width: this._w, height: Math.min(this._h, doc.currentBlock?.height ?? this._h) };
   }
}

export class MITDocumentNextPage extends MITDocumentElement {
   async render(doc: MITDocument): Promise<Block> {
      doc.nextPage();
      return { left: 0, top: 0, width: 0, height: 0 };
   }
}
export class MITDocumentText extends MITDocumentElement {
   text: string;
   textSize: number = 12;
   lineHeight: number = 1.2;
   leftPadding: number = 0;

   constructor(text: string) {
      super();
      this.text = text;
      return this;
   }

   setTextSize(size: number) {
      this.textSize = size;
      return this;
   }

   setLeftPadding(padding: number) {
      this.leftPadding = padding;
      return this;
   }


   async render(doc: MITDocument): Promise<Block> {
      let textSize = this?.textSize ?? 12;
      let block = doc.currentBlock ?? this.nextBlock(doc);
      if (block.height <= 0 || block.top < 0) {
         block = this.nextBlock(doc);
      }

      function fnWidth(tArr: string[]) {
         return doc.font?.widthOfTextAtSize(tArr.join(" "), textSize) ?? Utils.measureTextWidth(tArr.join(" "), textSize);
      }

      let page = doc.currentPage;
      if (!page) {
         throw new Error("No current page to render on");
      }
      let textA = this.text.split(" ");
      let rllTop = block.top;
      let maxWidth = 0;
      splitIntoPartsStable(textA, block.width - this.leftPadding, fnWidth).forEach((line) => {
         maxWidth = Math.max(maxWidth, fnWidth(line));
         if (rllTop < (block.top - block.height)) {
            block = doc.currentBlock ?? this.nextBlock(doc);
            page = doc.currentPage ?? doc.pdf.addPage();
            rllTop = block.top;
         }
         page!.drawText(line.join(" "), { x: block.left + this.leftPadding, y: rllTop - this.textSize, size: this.textSize, font: doc.font ?? undefined });
         rllTop -= this.lineHeight * this.textSize;
      });
      return { left: block.left, top: block.top - (block.top - rllTop), width: block.width, height: block.top - rllTop };
   }


}

export class MITDocumentTable extends MITDocumentElement {
   textSize: number = 10;
   data: { [key: string]: any }[];

   dataToShow: string[] = [];
   dataLineGrid: string[] = []
   dataHeader: { [key: string]: any } | null = null;



   constructor(data: { [key: string]: any }[]) {
      super();
      this.data = data;
      this.textSize = 10;
      return this;
   }


   async renderHeader(doc: MITDocument, dataCalc: { col: string, fitted: number }[][]): Promise<Block> {
      let blockPadding = 1;
      let block = doc.currentBlock ?? this.nextBlock(doc);
      let page = doc.currentPage;
      if (!page) {
         throw new Error("No current page to render on");
      }
      let rlTop = block.top;
      let lineHeight = this.textSize + 4
      dataCalc.forEach(row => {
         let left = 0;
         row.forEach(colCalc => {
            doc.currentPage?.drawRectangle({ x: block.left + left, y: rlTop, width: colCalc.fitted, height: - (lineHeight + 2 * blockPadding), borderColor: rgb(0.9, 0.9, 0.9) });
            page!.drawText(textCutEllipsis(doc, colCalc.col, this.textSize, colCalc.fitted - 2), { x: block.left + left + 2, y: rlTop - this.textSize - blockPadding, size: this.textSize, color: rgb(0, 0, .3) });
            left += colCalc.fitted;
         });
         rlTop -= lineHeight + 2 * blockPadding;
      });
      let resBlock = {
         left: block.left,
         top: rlTop,
         width: block.width,
         height: block.top - rlTop
      }
      doc.currentPage?.drawRectangle({ x: resBlock.left, y: resBlock.top, width: resBlock.width, height: resBlock.height, borderColor: rgb(.7, .7, .7), opacity: .8 });
      return resBlock;
   }

   async renderV2(doc: MITDocument): Promise<Block> {
      let block = doc.currentBlock ?? this.nextBlock(doc);
      let dataCalcs = calcTableSizes(doc, this.textSize, this.data, this.dataToShow, block.width, this.dataLineGrid);
      let blockPadding = 1;
      let data = this.data;
      let rlTop = block.top;
      let lineHeight = this.textSize + 4;
      if (this.notHeight(doc, block.top - rlTop, (lineHeight + 2 * blockPadding) * (dataCalcs.length + 1))) {
         block = doc.currentBlock ?? this.nextBlock(doc);
         rlTop = block.top;
      }
      rlTop -= (await this.renderHeader(doc, dataCalcs)).height;
      for (const row of data) {
         if (this.notHeight(doc, block.top - rlTop, (lineHeight + 2 * blockPadding) * dataCalcs.length)) {
            block = doc.currentBlock ?? this.nextBlock(doc);
            rlTop = block.top;
            rlTop -= (await this.renderHeader(doc, dataCalcs)).height;
         }
         dataCalcs.forEach((col) => {
            let left = 0;
            col.forEach(colCalc => {
               doc.currentPage?.drawRectangle({ x: block.left + left, y: rlTop, width: colCalc.fitted, height: - (lineHeight + 2 * blockPadding), borderColor: rgb(0.9, 0.9, 0.9), opacity: .5 });
               doc.currentPage?.drawText(String(textCutEllipsis(doc, row[colCalc.col], this.textSize, colCalc.fitted - 4)), { x: block.left + left + 2, y: rlTop - this.textSize - blockPadding, size: this.textSize });
               left += colCalc.fitted;
            });
            rlTop -= lineHeight + 2 * blockPadding;
         });
         doc.currentPage?.drawRectangle({ x: block.left, y: rlTop, width: block.width, height: (lineHeight + 2 * blockPadding) * dataCalcs.length, borderColor: rgb(.7, .7, .7), opacity: .8 });
      }
      return {
         left: block.left,
         top: block.top - (block.top - rlTop),
         width: block.width,
         height: (block.top - rlTop)
      };
   }

   async render(doc: MITDocument): Promise<Block> {
      return await this.renderV2(doc);
      // return await this.renderLegacy(doc);
   }

   async renderLegacy(doc: MITDocument): Promise<Block> {
      let block = doc.currentBlock ?? this.nextBlock(doc);
      if (this?.data.length === 0) {
         return { left: block.left, top: block.top, width: block.width, height: 0 };
      }

      let dataWH = [(Object.fromEntries(Object.keys(this.data[0] ?? {}).map(k => [k, k]))), ...this.data];
      let parts = Math.min(Object.keys(this.data[0] ?? {}).length || 1);

      // let colWidths = block.width / parts;
      let page = doc.currentPage;

      let colWidths = calcWidths(dataWH, parts);

      // 
      let maxColumns = findMaximumColumns(colWidths, block.width);
      colWidths = colWidths.slice(0, maxColumns);


      let total = colWidths.reduce((acc, w) => acc + w, 0);
      let scale = block.width / total;
      let scaledColWidths = colWidths.map(w => w * scale);

      let rllTop = block.top
      for (let i = 0; i < dataWH.length; i++) {
         let row = dataWH[i];
         if (this.notHeight(doc, block.top - rllTop, this.textSize * 1.2)) {
            block = doc.currentBlock ?? this.nextBlock(doc);
            page = doc.currentPage ?? doc.pdf.addPage();
            rllTop = block.top;
         }
         if (!page) {
            throw new Error("No current page to render on");
         }
         let left = block.left;
         Object.keys(row).forEach((key, idx) => {
            if (idx >= parts || idx >= maxColumns) {
               return;
            }
            left += scaledColWidths[idx - 1] || 0;
            let text = String(row[key]);
            page!.drawText(text, { x: left, y: rllTop - this.textSize, size: this.textSize });
         });
         rllTop -= this.textSize * 1.2;
      }

      doc.currentPage?.drawRectangle({
         x: block.left,
         y: block.top - (block.top - rllTop),
         width: block.width,
         height: (block.top - rllTop),
         borderColor: colorRandom(),
      })

      return {
         left: block.left,
         top: block.top - (block.top - rllTop),
         width: block.width,
         height: (block.top - rllTop)
      }
   }
}

export class MITDocumentVecMap extends MITDocumentElement {
   transformer: null | { rToS: (coord: [number, number]) => [number, number], sToR: (coord: [number, number]) => [number, number] };
   geojsonArr: any[];
   sig: { title: string, style?: any }[] = [];
   imgQuality: number = 0;
   title: string = "";

   constructor(geojson: any[] | any) {
      super();
      this.transformer = null;
      if (!Array.isArray(geojson)) {
         geojson = [geojson];
      }
      this.geojsonArr = geojson;
      return this;
   }

   createTransformer(geoJ: any, block: Block) {
      let projectedGeo = this.projectToDTK(geoJ);
      let [minX, minY, maxX, maxY] = bboxBuffer(bbox(projectedGeo), 10);

      let deltaX = maxX - minX;
      let deltaY = maxY - minY;

      let scale = Math.min(block.width / deltaX, block.height / deltaY) * 0.9;
      let offsetX = (block.width - deltaX * scale) / 2;
      let offsetY = (block.height - deltaY * scale) / 2;

      return {
         rToS: (coord: [number, number]) => {
            let [cX, cY,] = coord;
            return [
               (cX - minX) * scale + offsetX,
               (maxY - cY) * scale + offsetY,
            ] as [number, number];
         },
         sToR: (coord: [number, number]) => {
            let [cX, cY] = coord;
            // should be inverse of above
            return [
               (cX - offsetX) / scale + minX,
               maxY - (cY - offsetY) / scale,
            ] as [number, number];
         }
      }
   }

   setTitle(title: string) {
      this.title = title;
      return this;
   }

   setImage(nr: number) {
      this.imgQuality = nr;
      return this;
   }

   setSignature(sig: { title: string, style?: any }[]) {
      this.sig = sig;
      return this;
   }

   async render(doc: MITDocument): Promise<Block> {
      let blovk = doc.currentBlock ?? this.nextBlock(doc);
      if (this.notHeight(doc, 0, 500)) {
         blovk = doc.currentBlock ?? this.nextBlock(doc);
      }

      let transform = this.transformer ?? this.createTransformer(this.geojsonArr.find(geoJ => geoJ.mapBounds) ?? this.geojsonArr[0], blovk);

      if (this.imgQuality > 0) {
         let bottomRight = transform.sToR([blovk.width, 0]);
         let topLeft = transform.sToR([0, blovk.height]);
         let blob = await fetchImage(topLeft.concat(bottomRight) as [number, number, number, number], Math.round(blovk.width * this.imgQuality), Math.round(blovk.height * this.imgQuality))
         let image = await doc.pdf.embedJpg(blob);
         doc.currentPage?.drawImage(image, {
            x: blovk.left,
            y: blovk.top - blovk.height,
            width: blovk.width,
            height: blovk.height,
            opacity: 0.3,
         })
      }
      this.geojsonArr?.forEach(geoJ => {
         blovk = this.renderGj(doc, geoJ, transform);
      });

      if (this.title) {
         let textWidth = doc.font?.widthOfTextAtSize(this.title, 14) ?? Utils.measureTextWidth(this.title, 14);
         doc.currentPage?.drawRectangle({ x: blovk.left, y: blovk.top + blovk.height, width: textWidth + 6, height: -20, color: rgb(1, 1, 1), borderColor: rgb(0.9, 0.9, 0.9), opacity: 0.9, borderWidth: .1 });
         doc.currentPage?.drawText(this.title, { x: blovk.left + 3, y: blovk.top + blovk.height - 15, size: 14 });
      }

      if (this.sig.length) {
         doc.currentPage?.drawRectangle({ x: blovk.left, y: blovk.top + this.sig.length * 15 + 2, width: blovk.width / 4, height: -2 - this.sig.length * 15, color: rgb(1, 1, 1), borderColor: rgb(0.9, 0.9, 0.9), opacity: 0.9, borderWidth: .1 });

         this.sig.forEach((s, idx) => {
            doc.currentPage?.drawSvgPath(`M 0 0 h 10 v 10 h -10 Z`, { x: blovk.left + 3, y: - 2 + blovk.top + this.sig.length * 15 - idx * 15, ...(s.style ?? { color: rgb(0, 0, 0) }) });
            doc.currentPage?.drawText(s.title, { x: blovk.left + 16, y: blovk.top + this.sig.length * 15 - idx * 15 - 10, size: 10 });
         });
      }
      doc.currentPage?.drawRectangle({ x: blovk.left, y: blovk.top, width: blovk.width, height: blovk.height, borderColor: rgb(0.9, 0.9, 0.9), borderWidth: .1 });
      return blovk;
   }

   renderGj(doc: MITDocument, geoJ: any, transform: NonNullable<typeof this.transformer>): Block {
      if (!geoJ?.features?.length) {
         return doc.currentBlock ?? this.nextBlock(doc);
      }

      let pdfStyleFC = geoJ.pdfStyle
      let block = doc.currentBlock ?? this.nextBlock(doc);

      let projectedGeo = this.projectToDTK(geoJ);


      projectedGeo.features.forEach((feature: any) => {
         let ft = GenerateGeomUtils.geoJsonCoordsTransformer(feature, (coord: [number, number, number?]) => {
            let [px, py] = coord;
            let [x, y] = transform.rToS([px, py]);
            return [x, y];
         });
         let pdfStyle = feature.pdfStyle ?? pdfStyleFC ?? null;
         let clippedFt = cBBOXClip(ft, [0, 0, block.width, block.height]);
         if (!clippedFt) {
            return;
         }
         featureToSvgPaths(clippedFt).forEach((path: string) => {
            if (path.startsWith("P")) {
               let [_P, x, y] = path.split(" ").map(Number);
               doc.currentPage?.drawCircle({ x: block.left + x, y: block.top - y, ...(pdfStyle ?? { size: 3, color: rgb(0.8, 0, 0) }) });
               return
            } else if (path.startsWith("L")) {
               path = path.replace("L", "M");
               doc.currentPage?.drawSvgPath(path, { x: block.left, y: block.top, ...(pdfStyle ?? { borderColor: rgb(0.8, 0, 0), borderWidth: 1, borderOpacity: 0.6 }) });
               return;
            }
            doc.currentPage?.drawSvgPath(path, { x: block.left, y: block.top, ...(pdfStyle ?? { color: rgb(0.8, 0, 0), opacity: 0.4, borderColor: rgb(0.8, 0, 0), borderOpacity: .6 }) });
         });
      });

      return {
         left: block.left,
         top: block.top - block.height,
         width: block.width,
         height: block.height
      }
   }

   projectToDTK(geoJ: any) {
      return GenerateGeomUtils.geoJsonCoordsTransformer(geoJ, (coord: [number, number, number?]) => {
         let { x, y } = GenerateGeomUtils.convertFromLatLngToUTM({ lat: coord[1], lng: coord[0] });
         return [x, y];
      });

   }
}



export class MITDocumentTitle extends MITDocumentText {
   constructor(text: string) {
      super(text);
      this.textSize = 24;
      return this;
   }

   setTextSize(size: number) {
      this.textSize = size;
      return this;
   }

}

class MITDocumentHeader {
   constructor() {
      return this
   }
}
class MITDocumentFooter {

   constructor() {
      return this
   }
}
class Utils {
   static measureTextWidth(text: string, textSize: number): number {
      let context = document.createElement("canvas").getContext("2d");
      if (context) {
         context.font = `${textSize}px Arial`;
         return context.measureText(text).width;
      }
      return 0;
   }

   static splitLength(length: number, parts: number): number[] {
      let base = length / (parts || 0);
      return Array(parts).fill(base);
   }


}

function fetchImage(bbox: [number, number, number, number], width: number, height: number): Promise<ArrayBuffer> {
   let username = SettingsManager.getSystemSetting("datafordelerUsername")
   let password = SettingsManager.getSystemSetting("datafordelerPassword")
   let url = `https://wms.datafordeler.dk/GeoDanmarkOrto/orto_foraar/1.0.0/WMS?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&BBOX=${bbox.join(",")}&CRS=EPSG:25832&STYLES&FORMAT=image/jpeg&DPI=96&MAP_RESOLUTION=96&FORMAT_OPTIONS=dpi:96&Layers=orto_foraar&password=${password}&username=${username}&WIDTH=${width}&HEIGHT=${height}`
   return fetch(url)
      .then(res => res.arrayBuffer())
      .then(blob => {
         return blob;
      })
      .catch(err => {
         throw new Error("Failed to fetch image: " + err);
      });
}

function calcWidths(data: { [key: string]: any }[], parts: number): number[] {
   let widths: number[] = Array(parts).fill(0);
   for (let i = -1; i < parts; i++) {
      let arr = i === -1 ? Object.keys(data[0] ?? {}) : data.map(row => String(row[Object.keys(row)[i]]));
      arr.forEach((text) => {
         let w = Utils.measureTextWidth(text, 10);
         if (w > widths[i]) {
            widths[i] = w;
         }
      });
   }
   return widths;
}

function findMaximumColumns(col: number[], max: number): number {
   let acc = 0;
   for (let i = 0; i < col.length; i++) {
      if (acc + col[i] > max) {
         return i;
      }
      acc += col[i];
   }
   return col.length;
}

function bboxBuffer(bbox: [number, number, number, number, number?, number?], buffer: number): [number, number, number, number] {
   let [minX, minY, maxX, maxY, ..._] = bbox;
   return [minX - buffer, minY - buffer, maxX + buffer, maxY + buffer];
}

function cBBOXClip(feature: any, bbox: [number, number, number, number]): any {
   if (feature.geometry.type === "Point") {
      let [x, y] = feature.geometry.coordinates;
      let [minX, minY, maxX, maxY] = bbox;
      if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
         return feature;
      }
      return null;
   } else if (feature.geometry.type === "MultiPoint") {
      let [minX, minY, maxX, maxY] = bbox;
      let points = feature.geometry.coordinates.filter(([x, y]: [number, number]) => x >= minX && x <= maxX && y >= minY && y <= maxY);
      if (points.length === 0) {
         return null;
      }
      return { ...feature, geometry: { ...feature.geometry, coordinates: points } };
   }
   return bboxClip(feature, bbox);
}

function featureToSvgPaths(feature: any): string[] {
   let geom = feature.geometry.type === "GeometryCollection" ? feature.geometry.geometries : feature.geometry;
   if (feature.geometry.type === "GeometryCollection") {
      return geom.map((g: any) => featureToSvgPaths({ ...feature, geometry: g })).flat();
   }
   if (geom.type === "Point") {
      return ["P " + geom.coordinates.join(" ")];
   }
   if (geom.type === "MultiPoint") {
      return geom.coordinates.map((c: any) => "P " + c.join(" "));
   }
   if (geom.type === "LineString") {
      return ["L" + geom.coordinates.map((c: any) => c.join(" ")).join(" L ")];
   }
   if (geom.type === "MultiLineString") {
      return geom.coordinates.map((line: any) => "L" + line.map((c: any) => c.join(" ")).join(" L "));
   }
   if (geom.type === "Polygon") {
      return [geom.coordinates.map((ring: any) => "M" + ring.map((c: any) => c.join(" ")).join(" L ") + " Z").join(" ")];
   }
   if (geom.type === "MultiPolygon") {
      return geom.coordinates.map((poly: any) => poly.map((ring: any) => "M" + ring.map((c: any) => c.join(" ")).join(" L ") + " Z").join(" "));
   }
   return [];
}

function colorRandom() {
   return rgb(Math.random(), Math.random(), Math.random());
}

function calcTableSizes(doc: MITDocument, textSize: number, data: { [key: string]: any; }[], dataToShow: string[], maxWidth: number, dataLineGrid: string[]): { col: string, wordWidth: number, lineWidth: number, fitted: number }[][] {
   let rows = data
   if (dataToShow?.length) {
      rows = data.map(row => Object.fromEntries(dataToShow.map(k => [k, row[k]])));
   }

   let cols: { [key: string]: string[] } = {};
   rows.forEach(row => {
      Object.keys(row).forEach(key => {
         if (!cols[key]) {
            cols[key] = [];
         }
         cols[key].push(String(row[key]));
      });
   });
   let colWidths: { [key: string]: any }[] = [];
   Object.keys(cols).forEach(key => {
      colWidths.push({
         col: key,
         headerWidth: lineWidth(doc, [key], textSize) + 8, // padding 2 each side
         wordWidth: wordWidth(doc, cols[key], textSize) + 8, // padding 2 each side
         lineWidth: lineWidth(doc, cols[key], textSize) + 8, // padding 2 each side
      })
   });
   let x = splitIntoParts(colWidths, maxWidth, (arr) => arr.reduce((acc, col) => acc + (Math.max(col.lineWidth, col.headerWidth) || 0), 0));
   return x.map(part => fitColsToWidth(part as { col: string, headerWidth: number, wordWidth: number, lineWidth: number }[], maxWidth))
}

function wordWidth(doc: MITDocument, strings: string[], textSize: number): number {
   let x = strings.join(" ").split(" ").sort((a, b) => b.length - a.length);
   return doc.font?.widthOfTextAtSize(x[0], textSize) ?? Utils.measureTextWidth(x[0], textSize);
}

function lineWidth(doc: MITDocument, strings: string[], textSize: number): number {
   let x = strings.sort((a, b) => b.length - a.length);
   return doc.font?.widthOfTextAtSize(x[0], textSize) ?? Utils.measureTextWidth(x[0], textSize);
}

export function splitIntoPartsStable2<T>(arr: T[], maxLength: number, fn: (arr: T[]) => number): T[][] {
   let x = splitIntoParts(arr, maxLength, fn)
   let lastLine = x.at(-1)
   let len = 0
   if ((lastLine?.length ?? Infinity) < 3) {
      len = Math.abs(lastLine!.length - 3)
   }
   lastLine?.unshift(...x.at(-2)?.splice(-len) ?? [])
   return x
}

export function splitIntoParts<T>(arr: T[], maxWidth: number, fn: (arr: T[]) => number): T[][] {
   let lines: T[][] = [];
   let currentLine: T[] = [];
   arr.forEach(word => {
      if (fn([...currentLine, word]) < maxWidth) {
         currentLine.push(word);
      } else {
         lines.push(currentLine);
         currentLine = [word];
      }
   });
   return [...lines, currentLine];
}

function textSplitToMultiline(doc: MITDocument, str: string, textSize: number, maxWidth: number, options?: {
   maxLines?: number
}): string {
   let x = str.split(" ");
   function sWidth(arr: string[]) {
      return doc.font?.widthOfTextAtSize(arr.join(" "), textSize) ?? Utils.measureTextWidth(arr.join(" "), textSize);
   }
   let lines: string[][] = [];
   let currentLine: string[] = [];
   x.forEach(word => {
      if (sWidth([...currentLine, word]) < maxWidth || lines.length === (options?.maxLines ?? Infinity) - 1) {
         currentLine.push(word);
      } else {
         lines.push(currentLine);
         currentLine = [word];
      }
   });
   if (currentLine.length) {
      lines.push(currentLine);
   }
   return lines.map(line => line.join(" ")).join("\n");
}

function fitColsToWidth(cols: { col: string, headerWidth: number, wordWidth: number, lineWidth: number }[], maxWidth: number): { col: string, wordWidth: number, lineWidth: number, fitted: number }[] {
   let maxColWidth = cols.reduce((acc, col) => acc + Math.max(col.headerWidth, col.lineWidth), 0);
   let minColWidth = cols.reduce((acc, col) => acc + Math.max(col.headerWidth, col.wordWidth), 0);
   if (maxColWidth <= maxWidth) {
      let scale = (maxWidth - maxColWidth) / cols.length;
      return cols.map(col => ({ ...col, fitted: Math.max(col.headerWidth, col.lineWidth) + scale }));
   } else {
      let scale = maxWidth / minColWidth;
      return cols.map(col => ({ ...col, fitted: Math.max(col.headerWidth, col.wordWidth) * scale }));
   }
}

function textCutEllipsis(doc: MITDocument, str: string, textSize: number, maxWidth: number): string {
   let x = str.split?.("") ?? str.toString?.().split?.("") ?? [];
   let eplisisLength = doc.font?.widthOfTextAtSize("...", textSize) ?? Utils.measureTextWidth("...", textSize);
   function sWidth(arr: string[]) {
      return doc.font?.widthOfTextAtSize(arr.join(""), textSize) ?? Utils.measureTextWidth(arr.join(""), textSize);
   }

   let currentLine: string[] = [];
   if (sWidth(x) <= maxWidth) {
      return str;
   }
   for (let i = 0; i < x.length; i++) {
      let word = x[i];
      if (sWidth([...currentLine, word]) < maxWidth - eplisisLength) {
         currentLine.push(word);
      } else {
         return currentLine.join("") + "...";
      }
   }
   return currentLine.join("");
}


